export const COURSE_VIDEO_MAX_BYTES = 2 * 1024 * 1024 * 1024;
export const COURSE_VIDEO_MAX_DURATION_SEC = 3 * 60 * 60;
/** Storage allowance for a creator's reusable Course media. */
export const COURSE_MEDIA_STORAGE_SOFT_CAP_BYTES = 52_428_800_000;
/** Presigned segment/object URLs minted by the playback manifest proxy. */
export const COURSE_VIDEO_SIGNED_URL_TTL_SEC = 900;
/** Short-lived JWT for manifest/key access (bound to lesson + viewer). */
export const COURSE_VIDEO_PLAYBACK_SESSION_TTL_SEC = 900;
export const COURSE_VIDEO_PLAYBACK_TOKEN_TYP = 'course_playback';

/** Re-encode in browser when above this size (unless already optimal). */
export const COURSE_VIDEO_CLIENT_COMPRESS_THRESHOLD_BYTES = 80 * 1024 * 1024;
export const COURSE_VIDEO_CLIENT_MAX_HEIGHT = 720;
export const COURSE_VIDEO_CLIENT_TARGET_BITRATE = 2_500_000;

export const COURSE_VIDEO_TRANSCODE_QUEUE = 'course-video-transcode';

export const CourseVideoProcessingStatus = {
  NONE: 'none',
  UPLOADING: 'uploading',
  PROCESSING: 'processing',
  READY: 'ready',
  FAILED: 'failed',
} as const;

export type CourseVideoProcessingStatusValue =
  (typeof CourseVideoProcessingStatus)[keyof typeof CourseVideoProcessingStatus];

export type CourseVideoTranscodeJob = {
  lessonId: string;
  assetId: string;
  sourceKey: string;
};

export type CourseVideoProbe = {
  durationSec: number;
  width: number;
  height: number;
  sizeBytes: number;
  mimeType: string;
};

export function estimateVideoBitrateBps(probe: CourseVideoProbe): number {
  if (probe.durationSec <= 0) return 0;
  return Math.round((probe.sizeBytes * 8) / probe.durationSec);
}

/** Decide whether client-side compression before upload is worth the CPU cost. */
export function shouldCompressCourseVideo(probe: CourseVideoProbe): boolean {
  if (probe.sizeBytes <= COURSE_VIDEO_CLIENT_COMPRESS_THRESHOLD_BYTES) {
    if (probe.mimeType === 'video/mp4' && probe.height <= 1080) {
      const bitrate = estimateVideoBitrateBps(probe);
      return bitrate > 8_000_000;
    }
  }
  return (
    probe.sizeBytes > COURSE_VIDEO_CLIENT_COMPRESS_THRESHOLD_BYTES ||
    probe.height > 1080 ||
    !['video/mp4', 'video/webm'].includes(probe.mimeType)
  );
}

export function isVideoLessonProcessingBlocking(status: string | null | undefined): boolean {
  return (
    status === CourseVideoProcessingStatus.UPLOADING ||
    status === CourseVideoProcessingStatus.PROCESSING
  );
}

export function isVideoLessonPublishReady(status: string | null | undefined): boolean {
  if (!status || status === CourseVideoProcessingStatus.NONE) return true;
  return status === CourseVideoProcessingStatus.READY;
}

/** Rewrite an HLS manifest for proxied playback (presigned segments + optional AES key URI). */
export function rewriteHlsManifestForPlayback(params: {
  manifest: string;
  keyUri?: string | null;
  resolveSegmentUrl: (segmentRef: string) => string;
}): string {
  return params.manifest
    .split(/\r?\n/)
    .map((line) => {
      if (line.startsWith('#EXT-X-KEY:') && params.keyUri) {
        if (/URI="[^"]*"/.test(line)) {
          return line.replace(/URI="[^"]*"/, `URI="${params.keyUri}"`);
        }
        return `${line},URI="${params.keyUri}"`;
      }
      if (line.startsWith('#') || line.trim() === '') {
        return line;
      }
      return params.resolveSegmentUrl(line.trim());
    })
    .join('\n');
}
